import { useState } from "react";
import { Mail, Loader2 } from "lucide-react";
import { api } from "../lib/api";
import { apiErrorMessage } from "../lib/errors";

interface Props {
  open: boolean;
  onSaved: () => void;
}

export function AddEmailModal({ open, onSaved }: Props) {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  if (!open) return null;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    setError("");
    try {
      await api.post("/api/me/email", { email });
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-ink-900/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-xl">
        <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-brand-100 text-brand-600">
          <Mail className="h-6 w-6" />
        </div>
        <h2 className="mt-4 text-center text-lg font-semibold text-ink-900">
          Add a backup email
        </h2>
        <p className="mt-2 text-center text-sm text-ink-600">
          We use email for password recovery, billing receipts, and important
          account alerts. Your X handle alone isn't enough to recover your
          account if X login breaks.
        </p>
        <form onSubmit={handleSubmit} className="mt-5 space-y-3">
          <input
            className="input"
            type="email"
            required
            autoFocus
            placeholder="you@domain.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
          {error ? (
            <p className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700">
              {error}
            </p>
          ) : null}
          <button type="submit" className="btn-brand w-full" disabled={busy}>
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save email"
            )}
          </button>
        </form>
        <p className="mt-3 text-center text-2xs text-ink-400">
          We never share your email or send marketing without consent.
        </p>
      </div>
    </div>
  );
}
